import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@core/contexts/ProfileContext';
import { useVenue } from '@core/contexts/VenueContext';
import { ConsentScreen } from '@core/pages/ConsentScreen';
import { grantConsent } from '@core/api/consent';
import toast from 'react-hot-toast';

interface ConsentGateProps {
  children: ReactNode;
}

/**
 * ConsentGate - wrapper that shows ConsentScreen if user hasn't granted privacy consent
 * Required for 152-ФЗ compliance. Общий для обоих входов (мини-апп и PWA).
 */
export function ConsentGate({ children }: ConsentGateProps) {
  const { user, loading, refresh } = useUser();
  const { venue } = useVenue();
  const location = useLocation();

  // Правовые документы открыты всегда: экран согласия ссылается на них (в новой вкладке) — без этого
  // клиент без согласия видел бы вместо текста политики снова экран согласия.
  if (location.pathname.startsWith('/legal/')) {
    return <>{children}</>;
  }

  // Still loading user data
  if (loading) {
    return <>{children}</>;
  }

  // No user loaded (guest mode or error)
  if (!user) {
    return <>{children}</>;
  }

  // User hasn't granted privacy consent - show ConsentScreen
  if (!user.privacyConsentGranted) {
    const handleAccept = async (privacyConsent: boolean, marketingConsent: boolean) => {
      if (!privacyConsent) {
        toast.error('Согласие на обработку данных обязательно');
        return;
      }

      try {
        // Grant privacy consent (required)
        await grantConsent(user.id, 'PRIVACY', '1.0');

        // Grant marketing consent if checked (optional)
        if (marketingConsent) {
          await grantConsent(user.id, 'MARKETING', '1.0');
        }

        // Refresh user data to update consent status
        await refresh();

        toast.success('Спасибо! Добро пожаловать в программу лояльности');
      } catch (error) {
        console.error('Failed to grant consent:', error);
        toast.error('Ошибка при сохранении согласия. Попробуйте ещё раз');
      }
    };

    return (
      <ConsentScreen
        onAccept={handleAccept}
        venueSlug={venue?.slug || 'demo'}
      />
    );
  }

  // User has granted consent - show app
  return <>{children}</>;
}
